<script>
import bem from '@/utils/bem';
import CompanyTabLayout from '@/components/company/CompanyTabLayout.vue';
import { mapActions, mapState } from 'vuex';
import {
  LeftOutlined, RightOutlined, DeleteOutlined, EditOutlined, PlusOutlined,
} from '@ant-design/icons-vue';
import UploadImage from '@/components/ux/upload/Upload-image.vue';
import UploadImages from '@/components/ux/upload/Upload-images.vue';
import TiptapEditor from '@/components/ux/bundleEditor/Tiptap-editor.vue';
import { confirmDelete } from '@/utils/confirmDialog';

const b = bem('photo-galleries-page');

const generateSlug = (title) => (title || '')
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '') || `folder-${Date.now()}`;

export default {
  components: {
    CompanyTabLayout,
    UploadImage,
    UploadImages,
    TiptapEditor,
    LeftOutlined,
    RightOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
  },
  data() {
    return {
      selectedFolder: null,
      showFolderModal: false,
      editingFolder: null,
      folderForm: {
        title: '',
        description: '',
        url: '',
        metaTitle: '',
        metaDescription: '',
        coverImageAlt: '',
        isPublished: true,
      },
      coverFiles: [],
      showImageModal: false,
      imageForm: {
        title: '',
        description: '',
        url: '',
        metaTitle: '',
        metaDescription: '',
        alt: '',
        isPublished: true,
        licenseType: 'royalty_free',
        isForSale: false,
        price: 0,
        additionalDiscount: 0,
      },
      licenseTypeOptions: [
        { value: 'royalty_free', label: 'Royalty Free' },
        { value: 'rights_managed', label: 'Rights Managed' },
        { value: 'editorial', label: 'Editorial' },
        { value: 'cc_by', label: 'CC BY' },
        { value: 'cc_by_sa', label: 'CC BY-SA' },
        { value: 'cc_by_nc', label: 'CC BY-NC' },
        { value: 'cc_by_nc_sa', label: 'CC BY-NC-SA' },
        { value: 'cc_by_nd', label: 'CC BY-ND' },
        { value: 'cc_by_nc_nd', label: 'CC BY-NC-ND' },
        { value: 'cc0_public_domain', label: 'CC0 Public Domain' },
        { value: 'custom', label: 'Custom' },
      ],
      editingImage: null,
      imageFiles: [],
      imageFilesMobile: [],
      temporaryImages: [],
      isUploading: false,
      uploadedUids: new Set(),
      // AI auto-fill
      isAiAnalyzing: false,
      // Bulk upload progress
      bulkUploadProgress: null, // { current, total, filename, status }
      bulkUseAi: false,
      // Tags
      allTags: [],
      imageTags: [], // tags for currently editing image
      tagSearchText: '', // autocomplete search input
      showTagManager: false, // global tag manager panel
      // Image variant zoom preview
      variantPreview: null, // { src, label } for zoom modal
    };
  },
  computed: {
    ...mapState({
      folders: (state) => state.photoGallery.folders,
      images: (state) => state.photoGallery.images,
      currentCompany: (state) => state.companies.currentCompany,
    }),
    companyId() {
      return this.currentCompany?.id;
    },
    sortedFolders() {
      return [...this.folders].sort(
        (a, c) => (a.order || 0) - (c.order || 0),
      );
    },
    sortedImages() {
      return [...this.images].sort(
        (a, c) => (a.order || 0) - (c.order || 0),
      );
    },
    pendingFilesCount() {
      return this.temporaryImages.filter((f) => f.file).length;
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getFoldersAction: 'photoGallery/getFolders',
      createFolderAction: 'photoGallery/createFolder',
      updateFolderAction: 'photoGallery/updateFolder',
      deleteFolderAction: 'photoGallery/deleteFolder',
      updateFoldersOrderAction: 'photoGallery/updateFoldersOrder',
      getImagesAction: 'photoGallery/getImages',
      createImageAction: 'photoGallery/createImage',
      updateImageAction: 'photoGallery/updateImage',
      deleteImageAction: 'photoGallery/deleteImage',
      getLicenseInfoAction: 'photoGallery/getLicenseInfo',
      updateImagesOrderAction: 'photoGallery/updateImagesOrder',
      uploadImagesAction: 'photoGallery/uploadImages',
      aiAnalyzeFileAction: 'photoGallery/aiAnalyzeFile',
      aiAnalyzeImageAction: 'photoGallery/aiAnalyzeImage',
      bulkUploadWithProgressAction: 'photoGallery/bulkUploadWithProgress',
      fetchAllTagsAction: 'photoGallery/fetchAllTags',
      createTagAction: 'photoGallery/createTag',
      deleteTagAction: 'photoGallery/deleteTag',
      getImageTagsAction: 'photoGallery/getImageTags',
      setImageTagsAction: 'photoGallery/setImageTags',
      getTagImageCountAction: 'photoGallery/getTagImageCount',
    }),
    loadFolders(companyId) {
      if (companyId) this.getFoldersAction(companyId);
    },
    selectFolder(folder) {
      if (this.selectedFolder?.id === folder.id) {
        this.selectedFolder = null;
      } else {
        this.selectedFolder = folder;
        this.uploadedUids.clear();
        this.temporaryImages = [];
        this.getImagesAction(folder.id);
      }
    },
    openCreateFolderModal() {
      this.editingFolder = null;
      this.folderForm = {
        title: '',
        description: '',
        url: '',
        metaTitle: '',
        metaDescription: '',
        coverImageAlt: '',
        isPublished: true,
      };
      this.coverFiles = [];
      this.showFolderModal = true;
    },
    openEditFolderModal(folder) {
      this.editingFolder = folder;
      this.folderForm = {
        title: folder.title || '',
        description: folder.description || '',
        url: folder.url || '',
        metaTitle: folder.metaTitle || '',
        metaDescription: folder.metaDescription || '',
        coverImageAlt: folder.coverImageAlt || '',
        isPublished: folder.isPublished !== false,
      };
      this.coverFiles = folder.coverImageUrl
        ? [folder.coverImageUrl]
        : [];
      this.showFolderModal = true;
    },
    closeFolderModal() {
      this.showFolderModal = false;
      this.editingFolder = null;
    },
    onFolderTitleChange() {
      if (!this.editingFolder) {
        this.folderForm.url = generateSlug(this.folderForm.title);
      }
    },
    async saveFolderForm() {
      const coverFile = this.coverFiles.length > 0
        && this.coverFiles[0]?.file
        ? this.coverFiles[0]
        : null;
      if (this.editingFolder) {
        let coverImageUrl;
        if (!coverFile && this.coverFiles.length > 0) {
          const firstCover = this.coverFiles[0];
          coverImageUrl = typeof firstCover === 'string'
            ? firstCover
            : firstCover?.url;
        }
        await this.updateFolderAction({
          id: this.editingFolder.id,
          companyId: this.companyId,
          ...this.folderForm,
          coverFile,
          coverImageUrl,
        });
      } else {
        await this.createFolderAction({
          companyId: this.companyId,
          ...this.folderForm,
          order: this.folders.length,
          coverFile,
        });
      }
      this.closeFolderModal();
    },
    confirmDeleteFolder(folder) {
      this.deleteFolderAction({
        id: folder.id,
        companyId: this.companyId,
      });
      if (this.selectedFolder?.id === folder.id) {
        this.selectedFolder = null;
      }
    },
    moveFolderOrder(index, newIndex) {
      const reordered = [...this.sortedFolders];
      const [moved] = reordered.splice(index, 1);
      reordered.splice(newIndex, 0, moved);
      this.updateFoldersOrderAction({
        folders: reordered.map((f, i) => ({
          id: f.id,
          order: i,
        })),
        companyId: this.companyId,
      });
    },
    openCreateImageModal() {
      this.editingImage = null;
      this.imageForm = {
        title: '',
        description: '',
        url: '',
        metaTitle: '',
        metaDescription: '',
        alt: '',
        isPublished: true,
        licenseType: 'royalty_free',
        isForSale: false,
        price: 0,
        additionalDiscount: 0,
      };
      this.imageFiles = [];
      this.imageFilesMobile = [];
      this.imageTags = [];
      this.tagSearchText = '';
      this.showImageModal = true;
      // Preload tags so they're available for AI auto-fill or manual selection
      this.loadAllTags().catch(() => {});
    },
    openEditImageModal(image) {
      this.editingImage = image;
      this.imageForm = {
        title: image.title || '',
        description: image.description || '',
        url: image.url || '',
        metaTitle: image.metaTitle || '',
        metaDescription: image.metaDescription || '',
        alt: image.alt || '',
        isPublished: image.isPublished !== false,
        licenseType: image.licenseType || 'royalty_free',
        isForSale: image.isForSale || false,
        price: image.price || 0,
        additionalDiscount: image.additionalDiscount || 0,
      };
      this.imageFiles = image.imageUrl
        ? [image.imageUrl]
        : [];
      this.imageFilesMobile = image.imageUrlMobile
        ? [image.imageUrlMobile]
        : [];
      this.imageTags = [];
      this.tagSearchText = '';
      this.showImageModal = true;
      // Load tags for this image (parallel, non-blocking)
      if (image.id) {
        this.loadAllTags().catch(() => {});
        this.loadImageTags(image.id).catch(() => {});
      }
    },
    closeImageModal() {
      this.showImageModal = false;
      this.editingImage = null;
      this.imageTags = [];
      this.tagSearchText = '';
    },
    onImageTitleChange() {
      if (!this.editingImage) {
        this.imageForm.url = generateSlug(this.imageForm.title);
      }
    },
    async saveImageForm() {
      const imageFile = this.imageFiles.length > 0
        && this.imageFiles[0]?.file
        ? this.imageFiles[0]
        : null;
      if (this.editingImage) {
        let imageUrl;
        if (!imageFile && this.imageFiles.length > 0) {
          const first = this.imageFiles[0];
          imageUrl = typeof first === 'string'
            ? first
            : first?.url;
        }
        await this.updateImageAction({
          id: this.editingImage.id,
          folderId: this.selectedFolder.id,
          ...this.imageForm,
          imageFile,
          imageUrl,
        });
      } else {
        const created = await this.createImageAction({
          folderId: this.selectedFolder.id,
          companyId: this.companyId,
          ...this.imageForm,
          order: this.images.length,
          imageFile,
        });
        // Attach collected tags to the newly created image
        if (created?.id && this.imageTags.length > 0) {
          const tagIds = this.imageTags.map((t) => t.id);
          await this.setImageTagsAction({
            imageId: created.id,
            tagIds,
          });
        }
      }
      this.closeImageModal();
    },
    /**
     * Enhanced image deletion with license-aware confirmation.
     * Two paths:
     * 1. Licensed image (licensesCount > 0): warns admin that buyers retain 30-day
     *    download access after deletion (Soft-Delete Pattern). Backend sets isDeleted=true,
     *    deletedAt=NOW(). Cron cleans up S3 files after 30 days.
     * 2. Non-licensed image: simple confirmation, backend does hard delete.
     *
     * licensesCount may already be present from the GET /folder/:id response (preloaded).
     * If not, falls back to getLicenseInfoAction() which calls GET /photo-image/:id/license-info.
     *
     * @see photo-image.service.ts — remove() for dual-path delete logic
     * @see cron.service.ts — cleanupExpiredLicensedImages() for 30-day S3 cleanup
     */
    async confirmDeleteImage(image) {
      const licensesCount = image.licensesCount
      || await this.getLicenseInfoAction({ id: image.id });

      if (licensesCount > 0) {
        confirmDelete({
          title: 'Delete licensed image?',
          content: `This image has been purchased by ${licensesCount} user(s). `
            + 'Buyers will be able to download the original for 30 more days after deletion. '
            + 'After 30 days, the S3 files will be permanently removed.',
          onOk: () => this.deleteImageAction({
            id: image.id,
            folderId: this.selectedFolder.id,
          }),
        });
      } else {
        confirmDelete({
          title: 'Delete this image?',
          content: 'This action cannot be undone.',
          onOk: () => this.deleteImageAction({
            id: image.id,
            folderId: this.selectedFolder.id,
          }),
        });
      }
    },
    moveImageOrder(index, newIndex) {
      const reordered = [...this.sortedImages];
      const [moved] = reordered.splice(index, 1);
      reordered.splice(newIndex, 0, moved);
      this.updateImagesOrderAction({
        images: reordered.map((img, i) => ({
          id: img.id,
          order: i,
        })),
        folderId: this.selectedFolder.id,
      });
    },

    /**
     * AI Auto-fill: analyze current image and populate form fields.
     */
    async runAiAnalysis() {
      const hasFile = this.imageFiles.length > 0 && this.imageFiles[0]?.file;
      const hasExistingUrl = this.editingImage?.id
        && (this.editingImage?.imageUrl || this.editingImage?.imageUrlOriginal);
      if (!hasFile && !hasExistingUrl) return;

      this.isAiAnalyzing = true;
      try {
        let analysis = null;

        if (hasFile) {
          // New/replaced image: send the file directly via analyze-file endpoint
          analysis = await this.aiAnalyzeFileAction(this.imageFiles[0].file);
        } else if (hasExistingUrl) {
          // Existing image with URL: backend downloads from S3 via AWS SDK (no CORS issues)
          analysis = await this.aiAnalyzeImageAction(this.editingImage.id);
        }
        if (analysis) {
          // Apply AI suggestions to the form (only if field is empty)
          if (!this.imageForm.title && analysis.title) {
            this.imageForm.title = analysis.title;
          }
          if (!this.imageForm.description && analysis.description) {
            this.imageForm.description = analysis.description;
          }
          if (!this.imageForm.alt && analysis.alt) {
            this.imageForm.alt = analysis.alt;
          }
          if (!this.imageForm.metaTitle && analysis.metaTitle) {
            this.imageForm.metaTitle = analysis.metaTitle;
          }
          if (!this.imageForm.metaDescription && analysis.metaDescription) {
            this.imageForm.metaDescription = analysis.metaDescription;
          }
          // Auto-generate URL slug from AI title if empty
          if (!this.imageForm.url && analysis.title) {
            this.imageForm.url = analysis.title
              .toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '');
          }
          // Apply suggested tags (create missing + populate local list)
          if (analysis.suggestedTags?.length) {
            // Ensure allTags is loaded
            if (!this.allTags.length) {
              await this.loadAllTags();
            }
            // For each suggested tag: find existing or create new
            // eslint-disable-next-line no-restricted-syntax
            for (const tagName of analysis.suggestedTags) {
              const normalized = tagName.toLowerCase().trim();
              let existing = this.allTags.find(
                (t) => t.name.toLowerCase() === normalized,
              );
              if (!existing) {
                // Create the tag
                // eslint-disable-next-line no-await-in-loop
                existing = await this.createTagAction({ name: tagName });
                if (existing && !this.allTags.find((t) => t.id === existing.id)) {
                  this.allTags.push(existing);
                }
              }
              if (existing && !this.imageTags.find((t) => t.id === existing.id)) {
                this.imageTags.push(existing);
              }
            }
            // If editing existing image, save tags to DB now
            if (this.editingImage?.id) {
              const tagIds = this.imageTags.map((t) => t.id);
              await this.setImageTagsAction({
                imageId: this.editingImage.id,
                tagIds,
              });
            }
            // For new images, tags stay local and get saved after creation
          }
        }
      } finally {
        this.isAiAnalyzing = false;
      }
    },

    /**
     * Start bulk upload with progress tracking via SSE.
     */
    async startBulkUpload() {
      const filesToUpload = this.temporaryImages.filter((item) => item.file);
      if (!filesToUpload.length || !this.selectedFolder) return;

      this.isUploading = true;
      this.bulkUploadProgress = {
        current: 0, total: filesToUpload.length, filename: null, status: 'starting',
      };

      await this.bulkUploadWithProgressAction({
        files: filesToUpload,
        folderId: this.selectedFolder.id,
        companyId: this.companyId,
        useAi: this.bulkUseAi,
        onProgress: (current, total, filename, status) => {
          this.bulkUploadProgress = {
            current, total, filename, status,
          };
        },
      });

      this.isUploading = false;
      this.bulkUploadProgress = null;
      this.temporaryImages = [];
    },

    /* ---------------------------------------------------------------- */
    /*  Tags                                                             */
    /* ---------------------------------------------------------------- */

    async loadAllTags() {
      this.allTags = await this.fetchAllTagsAction();
    },

    async loadImageTags(imageId) {
      this.imageTags = await this.getImageTagsAction({ imageId });
    },

    /** Filtered list for autocomplete (exclude already-assigned) */
    filteredTags() {
      const assignedIds = new Set(this.imageTags.map((t) => t.id));
      const search = this.tagSearchText.toLowerCase().trim();
      return this.allTags
        .filter((t) => !assignedIds.has(t.id))
        .filter((t) => !search || t.name.toLowerCase().includes(search));
    },

    /** Add existing tag to current image */
    async addTagToImage(tag) {
      const currentIds = this.imageTags.map((t) => t.id);
      if (currentIds.includes(tag.id)) return;

      if (this.editingImage?.id) {
        // Existing image: save to DB immediately
        const updated = await this.setImageTagsAction({
          imageId: this.editingImage.id,
          tagIds: [...currentIds, tag.id],
        });
        if (updated) this.imageTags = updated;
      } else {
        // New image: just add locally, will be saved after creation
        this.imageTags.push(tag);
      }
      this.tagSearchText = '';
    },

    /** Create new tag and attach to current image */
    async createAndAttachTag() {
      if (!this.tagSearchText.trim()) return;
      const newTag = await this.createTagAction({ name: this.tagSearchText.trim() });
      if (!newTag) return;

      // Refresh global list
      if (!this.allTags.find((t) => t.id === newTag.id)) {
        this.allTags.push(newTag);
      }

      // Attach
      const currentIds = this.imageTags.map((t) => t.id);
      if (!currentIds.includes(newTag.id)) {
        if (this.editingImage?.id) {
          // Existing image: save to DB immediately
          const updated = await this.setImageTagsAction({
            imageId: this.editingImage.id,
            tagIds: [...currentIds, newTag.id],
          });
          if (updated) this.imageTags = updated;
        } else {
          // New image: just add locally
          this.imageTags.push(newTag);
        }
      }
      this.tagSearchText = '';
    },

    /** Remove tag from current image */
    async removeTagFromImage(tagId) {
      if (this.editingImage?.id) {
        // Existing image: save to DB immediately
        const newIds = this.imageTags.filter((t) => t.id !== tagId).map((t) => t.id);
        const updated = await this.setImageTagsAction({
          imageId: this.editingImage.id,
          tagIds: newIds,
        });
        if (updated) this.imageTags = updated;
      } else {
        // New image: just remove locally
        this.imageTags = this.imageTags.filter((t) => t.id !== tagId);
      }
    },

    /** Delete a global tag entirely */
    async deleteGlobalTag(tag) {
      const count = await this.getTagImageCountAction({ id: tag.id });
      const confirmed = window.confirm(
        `Delete tag "${tag.name}"? It will be removed from ${count} image(s).`,
      );
      if (!confirmed) return;
      await this.deleteTagAction({ id: tag.id });
      this.allTags = this.allTags.filter((t) => t.id !== tag.id);
      this.imageTags = this.imageTags.filter((t) => t.id !== tag.id);
    },

    /** Create a new global tag (from tag manager panel) */
    async createNewGlobalTag() {
      if (!this.tagSearchText.trim()) return;
      const newTag = await this.createTagAction({ name: this.tagSearchText.trim() });
      if (newTag && !this.allTags.find((t) => t.id === newTag.id)) {
        this.allTags.push(newTag);
        this.allTags.sort((a, c) => a.name.localeCompare(c.name));
      }
      this.tagSearchText = '';
    },

    toggleTagManager() {
      this.showTagManager = !this.showTagManager;
      if (this.showTagManager && !this.allTags.length) {
        this.loadAllTags();
      }
    },
  },
  watch: {
    companyId: {
      handler(newVal) {
        if (newVal) this.loadFolders(newVal);
      },
      immediate: true,
    },
    temporaryImages: {
      handler() {
        // Files are collected by the upload component.
        // Upload is triggered manually via "Start Upload" button.
      },
      deep: true,
    },
  },
};
</script>

<template>
  <!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
  <!-- eslint-disable vuejs-accessibility/label-has-for -->
  <CompanyTabLayout
    active-tab="photo-galleries"
  >
    <div :class="b()">
      <div :class="b('folders')">
        <div :class="b('folders-header')">
          <h3>Photo Folders</h3>
          <a-button
            type="primary"
            @click="openCreateFolderModal"
          >
            <template #icon><PlusOutlined /></template>
            Create folder
          </a-button>
        </div>
        <div :class="b('folders-list')">
          <div
            v-for="(folder, index) in sortedFolders"
            :key="folder.id"
            :class="b('folder-item', {
              selected: selectedFolder?.id === folder.id,
            })"
            @click="selectFolder(folder)"
          >
            <img
              v-if="folder.coverImageUrl"
              :class="b('folder-cover')"
              :src="folder.coverImageUrl"
              :alt="folder.coverImageAlt || folder.title"
            />
            <div :class="b('folder-info')">
              <span :class="b('folder-title')">
                {{ folder.title }}
              </span>
              <span :class="b('folder-desc')">
                {{ folder.description ? folder.description.replace(/<[^>]*>/g, '') : '' }}
              </span>
              <span :class="b('folder-url')">
                URL: {{ folder.url }}
              </span>
              <span :class="b('folder-count')">
                Images: {{ folder.imageCount || 0 }}
              </span>
              <span
                v-if="!folder.isPublished"
                :class="b('folder-unpublished')"
              >
                Unpublished
              </span>
            </div>
            <div
              :class="b('folder-actions')"
              @click.stop
            >
              <a-button
                size="small"
                @click="openEditFolderModal(folder)"
              >
                <template #icon>
                  <EditOutlined />
                </template>
              </a-button>
              <a-popconfirm
                title="Delete this folder?"
                @confirm="confirmDeleteFolder(folder)"
              >
                <a-button size="small" danger>
                  <template #icon>
                    <DeleteOutlined />
                  </template>
                </a-button>
              </a-popconfirm>
              <div :class="b('order-btns')">
                <a-button
                  v-if="index > 0"
                  size="small"
                  @click="moveFolderOrder(index, index - 1)"
                >
                  <template #icon>
                    <LeftOutlined />
                  </template>
                </a-button>
                <a-button
                  v-if="index < sortedFolders.length - 1"
                  size="small"
                  @click="moveFolderOrder(index, index + 1)"
                >
                  <template #icon>
                    <RightOutlined />
                  </template>
                </a-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="selectedFolder"
        :class="b('images-section')"
      >
        <div :class="b('images-header')">
          <h3>Images in "{{ selectedFolder.title }}"</h3>
          <div style="display: flex; gap: 8px;">
            <a-button @click="toggleTagManager">
              {{ showTagManager ? 'Hide' : 'Manage' }} Tags
            </a-button>
            <a-button
              type="primary"
              @click="openCreateImageModal"
            >
              <template #icon><PlusOutlined /></template>
              Add image
            </a-button>
          </div>
        </div>

        <!-- Global Tag Manager panel -->
        <div v-if="showTagManager" :class="b('tag-manager')">
          <h4>Global Tags</h4>
          <div :class="b('tag-manager-list')">
            <span
              v-for="tag in allTags"
              :key="tag.id"
              :class="b('tag-manager-item')"
            >
              {{ tag.name }}
              <span
                :class="b('tag-chip-remove')"
                @click="deleteGlobalTag(tag)"
              >&times;</span>
            </span>
            <span v-if="!allTags.length" style="color: #999; font-size: 13px;">No tags yet</span>
          </div>
          <div :class="b('tag-manager-create')">
            <a-input
              v-model:value="tagSearchText"
              placeholder="New tag name…"
              size="small"
              style="width: 200px"
              @keydown.enter.prevent="createNewGlobalTag"
            />
            <a-button
              size="small"
              type="primary"
              @click="createNewGlobalTag"
              :disabled="!tagSearchText.trim()"
            >
              Create Tag
            </a-button>
          </div>
        </div>
        <div :class="b('images-grid')">
          <div
            v-for="(image, index) in sortedImages"
            :key="image.id"
            :class="b('image-item')"
          >
            <img
              :class="b('image-thumb')"
              :src="image.imageUrlPreview"
              :alt="image.alt || image.title"
            />
            <div :class="b('image-info')">
              <span :class="b('image-title')">
                {{ image.title }}
              </span>
              <span :class="b('image-url')">
                URL: {{ image.url }}
              </span>
              <!--License badge — shows how many users purchased this image.
                   Helps admin make informed deletion decisions (licensed images use Soft-Delete).
                   licensesCount is preloaded by backend's findByFolder()
                   via a subquery on user_licensed_images. -->
              <a-tag v-if="image.licensesCount" color="blue" :class="b('license-badge')">
                {{ image.licensesCount }} license{{ image.licensesCount > 1 ? 's' : '' }}
              </a-tag>
            </div>
            <div :class="b('image-actions')">
              <a-button
                size="small"
                @click="openEditImageModal(image)"
              >
                <template #icon>
                  <EditOutlined />
                </template>
              </a-button>
              <a-button size="small" danger @click="confirmDeleteImage(image)">
                <template #icon>
                  <DeleteOutlined />
                </template>
              </a-button>
              <div :class="b('order-btns')">
                <a-button
                  v-if="index > 0"
                  size="small"
                  @click="moveImageOrder(
                    index, index - 1,
                  )"
                >
                  <template #icon>
                    <LeftOutlined />
                  </template>
                </a-button>
                <a-button
                  v-if="index < sortedImages.length - 1"
                  size="small"
                  @click="moveImageOrder(
                    index, index + 1,
                  )"
                >
                  <template #icon>
                    <RightOutlined />
                  </template>
                </a-button>
              </div>
            </div>
          </div>
        </div>
        <div :class="b('bulk-upload')">
          <h4>Bulk Upload Images</h4>
          <div :class="b('bulk-options')">
            <a-checkbox v-model:checked="bulkUseAi">
              AI auto-fill (title, description, alt, SEO)
            </a-checkbox>
          </div>
          <upload-image
            v-model:files="temporaryImages"
            :is-multiple="true"
            :initial-images="temporaryImages"
            :is-show-upload-list="true"
            :disabled="isUploading"
            :max-files="50"
          />
          <!-- Selected files count + Start button -->
          <div
            v-if="temporaryImages.length > 0 && !isUploading"
            :class="b('bulk-start')"
          >
            <span>{{ pendingFilesCount }} file(s) selected</span>
            <a-button
              type="primary"
              :disabled="!selectedFolder"
              @click="startBulkUpload"
            >
              Start Upload
            </a-button>
          </div>
          <!-- Bulk upload progress -->
          <div
            v-if="bulkUploadProgress"
            :class="b('bulk-progress')"
          >
            <a-progress
              :percent="Math.round(
                (bulkUploadProgress.current / bulkUploadProgress.total) * 100
              )"
              :status="bulkUploadProgress.status === 'completed'
                ? 'success' : 'active'"
            />
            <span :class="b('bulk-progress-text')">
              {{ bulkUploadProgress.current }} / {{ bulkUploadProgress.total }}
              <template v-if="bulkUploadProgress.filename">
                — {{ bulkUploadProgress.filename }}
                <template v-if="bulkUploadProgress.status === 'analyzing'">
                  (AI analyzing...)
                </template>
                <template v-else-if="bulkUploadProgress.status === 'uploading'">
                  (uploading...)
                </template>
              </template>
            </span>
          </div>
        </div>
      </div>

      <!-- Folder Modal -->
      <div
        v-if="showFolderModal"
        :class="b('modal-overlay')"
        @click.self="closeFolderModal"
      >
        <div :class="b('modal', { wide: true })">
          <h3>
            {{ editingFolder ? 'Edit Folder' : 'Create Folder' }}
          </h3>
          <div :class="b('modal-body')">
            <!-- Left column: form fields -->
            <div :class="b('modal-col-left')">
              <div :class="b('form-group')">
                <label for="folder-title">Title *</label>
                <a-input
                  id="folder-title"
                  v-model:value="folderForm.title"
                  @input="onFolderTitleChange"
                  placeholder="Folder title"
                />
              </div>
              <div :class="b('form-group')">
                <label for="folder-url">URL Slug *</label>
                <a-input
                  id="folder-url"
                  v-model:value="folderForm.url"
                  placeholder="folder-url-slug"
                />
              </div>
              <div :class="b('form-group')">
                <label for="folder-seo-title">
                  SEO Title
                </label>
                <a-input
                  id="folder-seo-title"
                  v-model:value="folderForm.metaTitle"
                  placeholder="SEO title (optional)"
                />
              </div>
              <div :class="b('form-group')">
                <label for="folder-seo-desc">
                  SEO Description
                </label>
                <a-textarea
                  id="folder-seo-desc"
                  v-model:value="folderForm.metaDescription"
                  :rows="2"
                  placeholder="SEO description (optional)"
                />
              </div>
              <div :class="b('form-group')">
                <label for="folder-cover-alt">
                  Cover Image Alt
                </label>
                <a-input
                  id="folder-cover-alt"
                  v-model:value="folderForm.coverImageAlt"
                  placeholder="Cover image alt text"
                />
              </div>
              <div :class="b('form-group')">
                <label for="folder-cover-img">
                  Cover Image
                </label>
                <UploadImages
                  id="folder-cover-img"
                  :is-multiple="false"
                  v-model:files="coverFiles"
                  :initial-images="coverFiles"
                />
              </div>
              <div :class="b('form-group')">
                <a-checkbox
                  v-model:checked="folderForm.isPublished"
                >
                  Published
                </a-checkbox>
              </div>
            </div>
            <!-- Right column: full WYSIWYG editor -->
            <div :class="b('modal-col-right')">
              <label>Description</label>
              <tiptap-editor
                :value="folderForm.description"
                @update:value="folderForm.description = $event"
                :height="500"
                :width="500"
                :max-height="700"
              />
            </div>
          </div>
          <div :class="b('modal-actions')">
            <a-button @click="closeFolderModal">
              Cancel
            </a-button>
            <a-button
              type="primary"
              @click="saveFolderForm"
            >
              {{ editingFolder ? 'Save' : 'Create' }}
            </a-button>
          </div>
        </div>
      </div>

      <!-- Image Modal -->
      <div
        v-if="showImageModal"
        :class="b('modal-overlay')"
        @click.self="closeImageModal"
      >
        <div :class="b('modal', { wide: true })">
          <div :class="b('modal-header')">
            <h3>
              {{ editingImage ? 'Edit Image' : 'Add Image' }}
            </h3>
            <a-button
              v-if="editingImage || (imageFiles.length > 0 && imageFiles[0]?.file)"
              :loading="isAiAnalyzing"
              @click="runAiAnalysis"
              :class="b('ai-btn')"
              size="small"
            >
              AI Auto-fill
            </a-button>
          </div>
          <div :class="b('modal-body')">
            <!-- Left column: form fields -->
            <div :class="b('modal-col-left')">
              <div :class="b('form-group')">
                <label for="img-title">Title</label>
                <a-input
                  id="img-title"
                  v-model:value="imageForm.title"
                  @input="onImageTitleChange"
                  placeholder="Image title"
                />
              </div>
              <div :class="b('form-group')">
                <label for="img-url">URL Slug *</label>
                <a-input
                  id="img-url"
                  v-model:value="imageForm.url"
                  placeholder="image-url-slug"
                />
              </div>
              <div :class="b('form-group')">
                <label for="img-alt">Alt Text</label>
                <a-input
                  id="img-alt"
                  v-model:value="imageForm.alt"
                  placeholder="Image alt text"
                />
              </div>
              <div :class="b('form-group')">
                <label for="img-seo-title">SEO Title</label>
                <a-input
                  id="img-seo-title"
                  v-model:value="imageForm.metaTitle"
                  placeholder="SEO title (optional)"
                />
              </div>
              <div :class="b('form-group')">
                <label for="img-seo-desc">
                  SEO Description
                </label>
                <a-textarea
                  id="img-seo-desc"
                  v-model:value="imageForm.metaDescription"
                  :rows="2"
                  placeholder="SEO description (optional)"
                />
              </div>
              <div :class="b('form-group')">
                <label for="img-license">License Type</label>
                <a-select
                  id="img-license"
                  v-model:value="imageForm.licenseType"
                  style="width: 100%"
                >
                  <a-select-option
                    v-for="opt in licenseTypeOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </a-select-option>
                </a-select>
              </div>

              <!-- Pricing / For Sale -->
              <div :class="b('form-group')">
                <a-checkbox v-model:checked="imageForm.isForSale">
                  Available for License Sale
                </a-checkbox>
              </div>
              <div v-if="imageForm.isForSale" :class="b('pricing-row')">
                <div :class="b('form-group')" style="flex: 1">
                  <label for="img-price">Price ($)</label>
                  <a-input-number
                    id="img-price"
                    v-model:value="imageForm.price"
                    :min="0"
                    :precision="2"
                    style="width: 100%"
                    placeholder="0.00"
                  />
                </div>
                <div :class="b('form-group')" style="flex: 1">
                  <label for="img-discount">Discount ($)</label>
                  <a-input-number
                    id="img-discount"
                    v-model:value="imageForm.additionalDiscount"
                    :min="0"
                    :precision="2"
                    style="width: 100%"
                    placeholder="0.00"
                  />
                </div>
                <div v-if="imageForm.price > 0" :class="b('price-preview')">
                  <span>Final: ${{
                  (imageForm.price - imageForm.additionalDiscount).toFixed(2)
                }}</span>
                </div>
              </div>

              <!-- Tags chip-input -->
              <div :class="b('form-group')">
                <label>Tags</label>
                <div :class="b('tag-chips')">
                  <span
                    v-for="tag in imageTags"
                    :key="tag.id"
                    :class="b('tag-chip')"
                  >
                    {{ tag.name }}
                    <span
                      :class="b('tag-chip-remove')"
                      @click="removeTagFromImage(tag.id)"
                    >&times;</span>
                  </span>
                  <div :class="b('tag-input-wrap')">
                    <a-auto-complete
                      v-model:value="tagSearchText"
                      :options="filteredTags().map(t => ({ value: t.name, label: t.name, tag: t }))"
                      placeholder="Search or create tag…"
                      size="small"
                      style="width: 180px"
                      @select="(val, option) => addTagToImage(option.tag)"
                      @keydown.enter.prevent="createAndAttachTag"
                    />
                    <a-button
                      v-if="tagSearchText.trim()
                        && !allTags.find(
                          t => t.name.toLowerCase()
                            === tagSearchText.trim().toLowerCase()
                        )"
                      size="small"
                      type="link"
                      @click="createAndAttachTag"
                    >
                      + Create "{{ tagSearchText.trim() }}"
                    </a-button>
                  </div>
                </div>
              </div>

              <div :class="b('form-group')">
                <label for="img-file">
                  Image File {{ editingImage ? '' : '*' }}
                </label>
                <UploadImages
                  id="img-file"
                  :is-multiple="false"
                  v-model:files="imageFiles"
                  :initial-images="imageFiles"
                />
              </div>
              <div :class="b('form-group')">
                <a-checkbox
                  v-model:checked="imageForm.isPublished"
                >
                  Published
                </a-checkbox>
              </div>

              <!-- Readonly fields visible when editing -->
              <template v-if="editingImage">
                <div :class="b('info-section')">
                  <h4>Image Info</h4>
                  <div
                    v-if="editingImage.imageSize"
                    :class="b('info-row')"
                  >
                    <span :class="b('info-label')">Size:</span>
                    <span>{{ editingImage.imageSize }}</span>
                  </div>
                  <div
                    v-if="editingImage.shortLinkCode"
                    :class="b('info-row')"
                  >
                    <span :class="b('info-label')">Short Link:</span>
                    <span>/i/{{ editingImage.shortLinkCode }}</span>
                  </div>
                  <div
                    v-if="editingImage.galleryName"
                    :class="b('info-row')"
                  >
                    <span :class="b('info-label')">Gallery:</span>
                    <span>{{ editingImage.galleryName }}</span>
                  </div>
                  <div
                    v-if="editingImage.uploadedAt"
                    :class="b('info-row')"
                  >
                    <span :class="b('info-label')">Uploaded:</span>
                    <span>{{ new Date(editingImage.uploadedAt).toLocaleString() }}</span>
                  </div>
                </div>

                <!-- Image variants preview -->
                <div
                  v-if="editingImage.imageUrlOriginal
                    || editingImage.imageUrlWatermarked
                    || editingImage.imageUrlPreview"
                  :class="b('variants-section')"
                >
                  <h4>Image Variants</h4>
                  <div :class="b('variants-grid')">
                    <div
                      v-if="editingImage.imageUrlOriginal"
                      :class="b('variant-item')"
                      @click="variantPreview = {
                        src: editingImage.imageUrlOriginal,
                        label: 'Original',
                      }"
                    >
                      <span :class="b('variant-label')">Original</span>
                      <div :class="b('variant-thumb-wrap')">
                        <img
                          :src="editingImage.imageUrlOriginal"
                          :class="b('variant-thumb')"
                          alt="Original"
                        />
                        <div :class="b('variant-zoom')">
                          <span class="anticon">&#x1F50D;</span>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="editingImage.imageUrlWatermarked"
                      :class="b('variant-item')"
                      @click="variantPreview = {
                        src: editingImage.imageUrlWatermarked,
                        label: 'Watermarked',
                      }"
                    >
                      <span :class="b('variant-label')">Watermarked</span>
                      <div :class="b('variant-thumb-wrap')">
                        <img
                          :src="editingImage.imageUrlWatermarked"
                          :class="b('variant-thumb')"
                          alt="Watermarked"
                        />
                        <div :class="b('variant-zoom')">
                          <span class="anticon">&#x1F50D;</span>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="editingImage.imageUrlPreview"
                      :class="b('variant-item')"
                      @click="variantPreview = {
                        src: editingImage.imageUrlPreview,
                        label: 'Preview',
                      }"
                    >
                      <span :class="b('variant-label')">Preview</span>
                      <div :class="b('variant-thumb-wrap')">
                        <img
                          :src="editingImage.imageUrlPreview"
                          :class="b('variant-thumb')"
                          alt="Preview"
                        />
                        <div :class="b('variant-zoom')">
                          <span class="anticon">&#x1F50D;</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
            <!-- Right column: full WYSIWYG editor -->
            <div :class="b('modal-col-right')">
              <label>Description</label>
              <tiptap-editor
                :value="imageForm.description"
                @update:value="imageForm.description = $event"
                :height="500"
                :width="500"
                :max-height="700"
              />
            </div>
          </div>
          <div :class="b('modal-actions')">
            <a-button @click="closeImageModal">
              Cancel
            </a-button>
            <a-button
              type="primary"
              @click="saveImageForm"
            >
              {{ editingImage ? 'Save' : 'Add' }}
            </a-button>
          </div>
        </div>
      </div>
    </div>
    <!-- Image variant zoom preview modal -->
    <a-modal
      :open="!!variantPreview"
      :title="variantPreview ? variantPreview.label : ''"
      :footer="null"
      :width="900"
      centered
      @cancel="variantPreview = null"
    >
      <div :class="b('variant-preview-body')">
        <img
          v-if="variantPreview"
          :src="variantPreview.src"
          :alt="variantPreview.label"
          :class="b('variant-preview-img')"
        />
      </div>
    </a-modal>
  </CompanyTabLayout>
</template>

<style scoped lang="scss">
.photo-galleries-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px;

  &__folders-header, &__images-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    h3 { margin: 0; }
  }

  &__folders-list { display: flex; flex-wrap: wrap; gap: 16px; }

  &__folder-item {
    width: 280px;
    border: 2px solid #d9d9d9;
    border-radius: 12px;
    padding: 12px;
    cursor: pointer;
    transition: border-color 0.2s;
    &:hover { border-color: #5f8aa4; }
    &_selected { border-color: #1890ff; background: #f0f7ff; }
  }

  &__folder-cover {
    width: 100%;
    height: 160px;
    object-fit: cover;
    border-radius: 8px;
    margin-bottom: 8px;
  }
  &__folder-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  &__folder-title { font-weight: 600; font-size: 16px; }
  &__folder-desc { font-size: 13px; color: #666; }
  &__folder-url { font-size: 12px; color: #999; }
  &__folder-count { font-size: 12px; color: #5f8aa4; }
  &__folder-unpublished {
    font-size: 11px;
    color: #ff4d4f;
    font-weight: 500;
  }
  &__folder-actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
    align-items: center;
  }
  &__order-btns {
    display: flex;
    gap: 4px;
    margin-left: auto;
  }
  &__images-section {
    border-top: 1px solid #eee;
    padding-top: 20px;
  }
  &__images-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  &__image-item {
    width: 240px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 8px;
  }

  &__image-thumb {
    width: 100%;
    height: 180px;
    object-fit: cover;
    border-radius: 6px;
    margin-bottom: 8px;
  }
  &__image-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 8px;
  }
  &__image-title { font-weight: 500; font-size: 14px; }
  &__image-url { font-size: 11px; color: #999; }
  &__license-badge { margin-top: 4px; width: fit-content; }
  &__image-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__bulk-upload {
    margin-top: 20px;
    padding: 16px;
    border: 1px dashed #d9d9d9;
    border-radius: 8px;
    h4 { margin-bottom: 12px; }
  }

  &__modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;
  }

  &__modal {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    width: 600px;
    max-height: 80vh;
    overflow-y: auto;
    h3 { margin-bottom: 16px; }

    &_wide {
      width: 1100px;
      max-width: 95vw;
      max-height: 90vh;
    }
  }

  &__modal-body {
    display: flex;
    gap: 24px;
    align-items: flex-start;
  }

  &__modal-col-left {
    flex: 0 0 480px;
    max-height: 75vh;
    overflow-y: auto;
    padding-right: 8px;
  }

  &__modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    h3 { margin: 0; }
  }

  &__modal-col-right {
    flex: 1;
    min-width: 0;

    > label {
      display: block;
      margin-bottom: 4px;
      font-weight: 500;
      font-size: 14px;
    }
  }

  &__form-group {
    margin-bottom: 16px;
    label {
      display: block;
      margin-bottom: 4px;
      font-weight: 500;
      font-size: 14px;
    }
  }

  &__modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 20px;
  }

  &__info-section {
    margin-top: 16px;
    padding: 12px;
    background: #fafafa;
    border-radius: 8px;
    border: 1px solid #f0f0f0;
    h4 { margin: 0 0 8px 0; font-size: 13px; color: #666; }
  }

  &__info-row {
    display: flex;
    gap: 8px;
    font-size: 13px;
    margin-bottom: 4px;
  }

  &__info-label {
    font-weight: 500;
    color: #888;
    min-width: 80px;
  }

  &__variants-section {
    margin-top: 16px;
    h4 { margin: 0 0 8px 0; font-size: 13px; color: #666; }
  }

  &__variants-grid {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__variant-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    cursor: pointer;
  }

  &__variant-label {
    font-size: 11px;
    color: #999;
    font-weight: 500;
  }

  &__variant-thumb-wrap {
    position: relative;
    border-radius: 6px;
    overflow: hidden;

    &:hover {
      .photo-galleries-page__variant-zoom {
        opacity: 1;
      }
      .photo-galleries-page__variant-thumb {
        filter: brightness(0.7);
      }
    }
  }

  &__variant-thumb {
    width: 120px;
    height: 90px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid #eee;
    transition: filter 0.2s;
  }

  &__variant-zoom {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;

    span {
      font-size: 24px;
      filter: grayscale(1) brightness(10);
    }
  }

  &__variant-preview-body {
    display: flex;
    justify-content: center;
    align-items: center;
    max-height: 70vh;
    overflow: auto;
  }

  &__variant-preview-img {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 4px;
  }

  &__bulk-options {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 12px 16px;
    background: #f6f8fa;
    border-radius: 8px;
    margin-bottom: 12px;
    border: 1px solid #e8ecf0;
  }

  &__bulk-start {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    margin-bottom: 12px;
    background: #f6ffed;
    border-radius: 8px;
    border: 1px solid #b7eb8f;
  }

  &__bulk-progress {
    margin-bottom: 16px;
    padding: 12px 16px;
    background: #f0f7ff;
    border-radius: 8px;
    border: 1px solid #d0e3f7;
  }

  &__bulk-progress-text {
    display: block;
    font-size: 12px;
    color: #555;
    margin-top: 6px;
  }

  /* Tags */

  &__tag-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    padding: 6px 0;
  }

  &__tag-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    background: #e6f7ff;
    border: 1px solid #91d5ff;
    border-radius: 12px;
    font-size: 12px;
    color: #096dd9;
    white-space: nowrap;
  }

  &__tag-chip-remove {
    cursor: pointer;
    font-weight: bold;
    color: #999;
    font-size: 14px;
    line-height: 1;
    &:hover { color: #f5222d; }
  }

  &__tag-input-wrap {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__tag-manager {
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;

    h4 {
      margin: 0 0 10px 0;
      font-size: 14px;
      font-weight: 600;
      color: #333;
    }
  }

  &__tag-manager-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 12px;
  }

  &__tag-manager-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    background: #f5f5f5;
    border: 1px solid #d9d9d9;
    border-radius: 12px;
    font-size: 12px;
    color: #333;
  }

  &__tag-manager-create {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__ai-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    border: none;
    &:hover, &:focus {
      background: linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%);
      color: #fff;
    }
    &[disabled] {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  &__pricing-row {
    display: flex;
    gap: 12px;
    align-items: flex-end;
    margin-bottom: 12px;
  }
  &__price-preview {
    padding: 6px 12px;
    background: #f0f7ff;
    border-radius: 6px;
    font-weight: 600;
    color: #1890ff;
    white-space: nowrap;
    margin-bottom: 24px;
  }
}
</style>
